import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Radio,
  DatePicker,
  Select,
  Divider,
  Row,
  Col,
  Progress,
  Tag,
} from 'antd';
import { useTranslation } from 'react-i18next';
import './BasicInfoSection.scss';
import { fetchDistrict } from 'services/orderService';
import i18n from 'i18n';
import { convertObjectToOptions } from 'utils/commonUtils';
import { CommonOption } from 'types/common';
import { MONTH_YEAR_FORMAT } from 'constants/consts';
import TopAlignCheckbox from 'components/topAlignCheckbox/TopAlignCheckbox';
import { useSession } from 'context/SessionContext';
import dayjs from 'dayjs';
import { SecurityUtils } from 'utils/securityUtils';

interface BasicInfoSectionProps {
  isReadonly?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onLoadError?: (error: boolean) => void;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  isReadonly = false,
  onLoadingChange,
  onLoadError,
}) => {
  const { t } = useTranslation('registration');
  const lang = i18n.language;
  const [districtOptions, setDistrictOptions] = useState<CommonOption[]>([]);
  const form = Form.useFormInstance();
  const setAsPickupValue = Form.useWatch('setAsPickup', form);
  const {session} = useSession();


  const minBirthDate = dayjs().subtract(120, 'year').startOf('month');
  const maxBirthDate = dayjs().subtract(18, 'year').endOf('month');
  const defaultViewDate = dayjs().subtract(18, 'year');
  const [panelYear, setPanelYear] = useState<number>(maxBirthDate.year());
  const [panelMode, setPanelMode] = useState<string>('month');

  const handleDistrictChange = (value: string, option: any) => {
    form.setFieldsValue({ district: option.label });
  };

  const fullAddress =
    form.getFieldValue('address') && form.getFieldValue('district')
      ? form.getFieldValue('address') + ', ' + form.getFieldValue('district')
      : '-';

  const handleNameInput = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const value = e.target.value.replace(/[^a-zA-Z\u4e00-\u9fa5\s]/g, '');
    form.setFieldsValue({ [fieldName]: value });
  };

  useEffect(() => {
    const initData = async () => {
      try {
        // Start loading
        onLoadingChange?.(true);
        onLoadError?.(false);

        const districtData = await fetchDistrict('/common', lang);
        setDistrictOptions(convertObjectToOptions(districtData));

        // End loading
        onLoadingChange?.(false);
      } catch (err) {
        console.error('Failed to obtain district data:', err);
        onLoadError?.(true);
        onLoadingChange?.(false);
      }
    };
    initData();
  }, [lang, onLoadingChange, onLoadError, session?._tk]);

  return (
    <div className="basic-info-section-container">
      <div className="vls-section-title">
        {isReadonly ? t('reviewAndConfirm') : t('personalInformation')}
      </div>

      {isReadonly && (
        <Progress
          percent={60}
          className="registration-progress"
          showInfo={false}
        />
      )}

      {/* Title */}
      <Form.Item
        required
        name="titleCode"
        label={t('titleLabel')}
        rules={[
          {
            required: true,
            message: t('titleRequired'),
          },
        ]}
      >
        {isReadonly ? (
          <span className="readonly-value">
            {form?.getFieldValue('titleCode') === 'mr' && t('titleMr')}
            {form?.getFieldValue('titleCode') === 'ms' && t('titleMs')}
            {form?.getFieldValue('titleCode') === 'mrs' && t('titleMrs')}
          </span>
        ) : (
          <Radio.Group className="title-radio-group">
            <Radio value="mr">{t('titleMr')}</Radio>
            <Radio value="ms">{t('titleMs')}</Radio>
            <Radio value="mrs">{t('titleMrs')}</Radio>
          </Radio.Group>
        )}
      </Form.Item>

      {/* Name */}
      {isReadonly ? (
        <div>
          <Form.Item
            required
            label={t('fullNameLabel')}
            rules={[
              {
                required: true,
                message: t('lastNameRequired'),
              },
            ]}
          >
            {form?.getFieldValue('lastName') +
              ' ' +
              form?.getFieldValue('firstName')}
          </Form.Item>
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* Last Name */}
            <Form.Item
              required
              name="lastName"
              label={t('lastNameLabel')}
              rules={[
                {
                  required: true,
                  message: t('lastNameRequired'),
                },
              ]}
            >
              <Input
                placeholder={t('lastNamePlaceholder')}
                onChange={(e) => handleNameInput(e, 'lastName')}
              />
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            {/* First Name */}
            <Form.Item
              name="firstName"
              label={t('firstNameLabel')}
              rules={[
                {
                  required: true,
                  message: t('firstNameRequired'),
                },
              ]}
            >
              <Input
                placeholder={t('firstNamePlaceholder')}
                onChange={(e) => handleNameInput(e, 'firstName')}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]}>
        <Col sm={12} xs={24}>
          {/* Birthday */}
          <Form.Item
            name="birthDay"
            label={t('birthdayLabel')}
            rules={[
              {
                required: true,
                message: t('birthdayRequired'),
              },
            ]}
          >
            {isReadonly ? (
              <span className="readonly-value">
                {form?.getFieldValue('birthDay')?.format(MONTH_YEAR_FORMAT) ||
                  '-'}
              </span>
            ) : (
              <DatePicker
                placeholder={MONTH_YEAR_FORMAT}
                picker="month"
                format="MM/YYYY"
                className="birthday-picker"
                classNames={{ popup: { root: 'hide-disabled-dates-dropdown' }}}
                defaultPickerValue={defaultViewDate}
                onPanelChange={(value, mode) => {
                  setPanelYear(value.year());
                  setPanelMode(mode);
                }}

                panelRender={(panelNode) => {
                  return (
                    <div className={`birthday-picker-popup-wrapper mode-${panelMode} year-${panelYear}`}>
                      {panelNode}
                    </div>
                  );
                }}
                disabledDate={(current) => {
                  if (!current) return false;
                  return current.isBefore(minBirthDate) || current.isAfter(maxBirthDate);
                }}
              />
            )}
          </Form.Item>
          {!isReadonly && <div className="field-note">{t('birthdayNote')}</div>}
        </Col>
      </Row>

      <Divider />
      <div className="section-subtitle">{t('contactInformation')}</div>

      <Row gutter={[16, 16]}>
        <Col sm={12} xs={24}>
          {/* Email */}
          <Form.Item
            name="email"
            label={t('emailLabel')}
            rules={[
              {
                required: true,
                message: t('emailRequired'),
              },
              {
                type: 'email',
                message: t('emailInvalid'),
              },
            ]}
          >
            {isReadonly ? (
              <span className="readonly-value">
                {form?.getFieldValue('email') || '-'}
              </span>
            ) : (
              <Input placeholder={t('emailPlaceholder')} type="email" />
            )}
          </Form.Item>
          {!isReadonly && (
            <div className="field-note">{t('emailVerificationNote')}</div>
          )}
        </Col>
      </Row>

      <Divider />

      {/* Address */}
      <div className="section-subtitle">{t('addressLabel')}</div>
      {isReadonly ? (
        <Form.Item
          name="fullAddress"
          label={
            isReadonly ? (
              <div>
                {t('addressLabel')}
                {form?.getFieldValue('setAsPickup') && (
                  <Tag className="pick-up-address-tag">
                    {t('defaultPickupAndDeliveryAddress')}
                  </Tag>
                )}
              </div>
            ) : (
              t('addressLabel')
            )
          }
        >
          <div className="full-address">{fullAddress}</div>
        </Form.Item>
      ) : (
        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* Address */}
            <Form.Item
              name="address"
              label={t('addressLabel')}
              required={!isReadonly && !!setAsPickupValue}
              getValueFromEvent={(e) => SecurityUtils.sanitize(e.target.value, 255)}
              rules={[
                {
                  validator: (_, value) => {
                    const setAsPickup = form.getFieldValue('setAsPickup');
                    if (setAsPickup && !value) {
                      return Promise.reject(new Error(t('addressRequired')));
                    }
                    if (value && value.length < 10) {
                      return Promise.reject(new Error(t('addressMinLength')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder={t('addressPlaceholder')} />
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
              name="districtCode"
              label={t('districtLabel')}
              dependencies={['setAsPickup']}
              required={!isReadonly && !!setAsPickupValue}
              rules={[
                {
                  validator: (_, value) => {
                    const setAsPickup = form.getFieldValue('setAsPickup');
                    if (setAsPickup && !value) {
                      return Promise.reject(new Error(t('districtRequired')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                placeholder={t('districtPlaceholder')}
                className="district-select"
                showSearch
                options={districtOptions}
                optionFilterProp="label"
                onChange={handleDistrictChange}
              />
            </Form.Item>
            <Form.Item name="district" noStyle>
              <Input type="hidden" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* set as pickup and delivery address */}
      <Form.Item
        name="setAsPickup"
        valuePropName="checked"
        className="communication-checkbox-item"
        noStyle={isReadonly}
      >
        {!isReadonly && (
          <TopAlignCheckbox>
            {t('setAsPickupAndDeliveryAddress')}
          </TopAlignCheckbox>
        )}
      </Form.Item>

      <Divider />

      <div className="section-subtitle">{t('earnAsiaMiles')}</div>

      <Row gutter={[16, 16]}>
        <Col sm={12} xs={24}>
          {/* Asia Miles Number */}
          <Form.Item
            name="asiaMile"
            label={t('asiaMilesNumberLabel')}
            rules={[
              {
                pattern: /^\d{10}$/,
                message: t('asiaMilesNumberInvalid'),
              },
            ]}
          >
            {isReadonly ? (
              <span className="readonly-value">
                {form?.getFieldValue('asiaMile') || '-'}
              </span>
            ) : (
              <Input
                type="tel"
                placeholder={t('asiaMilesNumberPlaceholder')}
                maxLength={10}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default BasicInfoSection;